/* eslint-disable react/prop-types */
import React, { useRef, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import { useReactToPrint } from 'react-to-print'
import PrintTwoToneIcon from '@mui/icons-material/PrintTwoTone'

import { cancelPrintAction, printAction } from '../../../../utils/actions'
import { getFormValid, getJobSettingsState, getSettingsState, selectAll } from '../../store/slices'

import { createSelector } from '@reduxjs/toolkit'
import { Button, PrintWrapper, SvgIcon } from '../../../../components'
import { COUNTRY, LOADING } from '../../../../utils/constants'
import { getCalcInputState, getUIState } from '../../../../utils/selectors'
import { APP_CONFIG } from '../../common/constants'
import {
  RenderIf,
  getNestedObject,
  getText,
  isArray,
  isMemberTypeSolidSawn,
  shouldDisplayNumberOfPlies,
} from '../../common/fns'
import { joist as joistData, truss as trussData } from '../../common/json/carried-member.json'
import { girder as girderData, header as headerData } from '../../common/json/carrying-member.json'
import {
  connectionOptions,
  countryOptions,
  downloadDurationTypeOptions,
  hanger as hangerData,
  hangerTypeOptions,
  upliftLoadDurationTypeOptions,
} from '../../common/json/common.json'
import { getHangerState, getResultState } from '../../store/selectors'
import { JobTally, PrintTable, Result } from './components'

const getMaterialText = (list, type, country, value) => {
  const options = list[type]?.[country] || list[type]
  return getText(options, value)
}

const selectPrintButtonState = createSelector(
  getFormValid,
  (state) => getUIState(state).loading,
  (state) => getResultState(state).loading,
  (isValid, uiLoading, resultLoading) => ({
    disabled: !isValid || resultLoading === LOADING.PENDING || uiLoading === LOADING.PENDING,
  }),
)

const PrintButton = ({ onClick }) => {
  const { disabled } = useSelector(selectPrintButtonState)

  return (
    <Button
      variant="topNav"
      className="button topNav__button"
      onClick={onClick}
      disabled={disabled}
    >
      <PrintTwoToneIcon className="material-icons-two-tone material-icons-two-tone--noinvert" />
      <span className="button__text">Print</span>
    </Button>
  )
}

function getPrintColumns({ header, joist, girder, truss, jack, leftHip, rightHip, settings }) {
  const memberColumns = (member, dataset, name) => {
    if (!member) return []

    const { type, material, width, depth, isRough } = member

    const widthHeader = {
      joist: 'Width',
      header: 'Width',
    }
    const heightHeader = {
      joist: 'Depth',
      header: 'Depth',
      girder: 'Bottom Chord Height',
    }

    const getOptionsByKey = (opts, ...keys) => (isArray(opts) ? opts : getNestedObject(opts, keys))

    const widthOptions = () => {
      const data = dataset.widthData.find(
        (item) => item.type === type && item.material.includes(material),
      )

      return data ? getOptionsByKey(data.options, [isRough ? 'rough' : 'default']) : []
    }

    const depthOptions = () => {
      const data = dataset.depthData.find(
        (item) =>
          item.type === type &&
          item.material.includes(material) &&
          // TODO: Need to have dynamic condition for dependent data
          (item.width || [width]).includes(width),
      )
      return data ? getOptionsByKey(data.options, [isRough ? 'rough' : 'default']) : []
    }

    const getNumberOfPliesOptions = () => {
      const data = dataset.pliesData.find(
        (item) =>
          item.type === type &&
          item.material.includes(material) &&
          (item.width || [width]).includes(width) &&
          (item.depth || [depth]).includes(depth),
      )
      return data ? getOptionsByKey(data.options, []) : []
    }

    return [
      {
        Header: 'Member Type',
        accessor: 'type',
        Cell: ({ value }) => <span>{getText(dataset.memberTypeData, value)}</span>,
      },
      {
        Header: 'Lumber Species',
        accessor: 'material',
        Cell: ({ value }) => (
          <span>{getMaterialText(dataset.materialData, type, settings.country, value)}</span>
        ),
      },
      {
        Header: widthHeader[name] ? widthHeader[name] : 'Bottom Chord Width',
        accessor: 'width',
        Cell: ({ value }) => <span>{getText(widthOptions(), value)}</span>,
      },
      {
        Header: heightHeader[name] ? heightHeader[name] : 'Heel Height',
        accessor: 'depth',
        Cell: ({ value }) =>
          heightHeader[name] ? <span>{getText(depthOptions(), value)}</span> : <span>{value}</span>,
      },
      ...(shouldDisplayNumberOfPlies(type, getNumberOfPliesOptions(), isRough)
        ? [
            {
              Header: 'Number of Plies',
              accessor: 'ply',
            },
          ]
        : []),
      {
        Header: 'Member ID',
        accessor: 'memberID',
      },
    ]
  }

  const jobSettingsColumns = [
    {
      Header: 'Hanger Type',
      accessor: 'hangerType',
      Cell: ({ value }) => <span>{getText(hangerTypeOptions, value)}</span>,
    },
    {
      Header: 'Download Duration',
      accessor: 'downloadDurationType',
      Cell: ({ value }) => (
        <span>{getText(downloadDurationTypeOptions[settings.country], value)}</span>
      ),
    },
    {
      Header: 'Uplift Duration',
      accessor: 'upliftLoadDurationType',
      Cell: ({ value }) => (
        <span>{getText(upliftLoadDurationTypeOptions[settings.country], value)}</span>
      ),
    },
    {
      Header: 'Job ID',
      accessor: 'jobID',
    },
    {
      Header: 'Quantity',
      accessor: 'quantity',
    },
  ]

  const settingColumns = [
    {
      Header: 'Country',
      accessor: 'country',
      Cell: ({ value }) => <span>{getText(countryOptions, value)}</span>,
    },
    {
      Header: 'Connection Type',
      accessor: 'connectionType',
      Cell: ({ value }) => <span>{getText(connectionOptions, value)}</span>,
    },
  ]

  const isRoughColumn = [
    {
      Header: 'Rough Lumber',
      accessor: 'isRough',
      Cell: ({ value }) => <span>{value ? 'Yes' : 'No'}</span>,
    },
  ]

  const loadsColumns = [
    {
      Header: `Download (${settings.country === COUNTRY.USA ? 'ASD' : 'LSD'})`,
      accessor: 'load',
    },
    {
      Header: `Uplift (${settings.country === COUNTRY.USA ? 'ASD' : 'LSD'})`,
      accessor: 'uplift',
    },
  ]

  const angleColumns = [
    {
      Header: 'Skew Type',
      accessor: 'skewType',
      Cell: ({ value }) => <span>{getText(hangerData.skewType.options, value)}</span>,
    },
    {
      Header: 'Skew (Degrees)',
      accessor: 'skewAngle',
    },
    {
      Header: 'Slope Type',
      accessor: 'slopeType',
      Cell: ({ value }) => <span>{getText(hangerData.slopeType.options, value)}</span>,
    },
    {
      Header: 'Slope (Degrees)',
      accessor: 'slopeAngle',
    },
  ]

  const headerColumns = memberColumns(header, headerData, 'header').concat(isRoughColumn)

  const joistColumns = memberColumns(joist, joistData, 'joist')
    .concat(isRoughColumn)
    .concat(loadsColumns)

  const girderColumns = memberColumns(girder, girderData, 'girder').concat([
    {
      Header: 'Top Chord Ply',
      accessor: 'topChordPly',
    },
    ...(isMemberTypeSolidSawn(girder?.type)
      ? [
          {
            Header: 'Vertical Width (King Post)',
            accessor: 'kingWidth',
          },
          {
            Header: 'Total Height',
            accessor: 'kingHeight',
          },
        ]
      : []),
  ])

  const trussColumns = memberColumns(truss, trussData, 'truss').concat(loadsColumns)

  const jackColumns = memberColumns(jack, trussData, 'jack')
    .concat(loadsColumns)
    .concat([
      {
        Header: 'Slope Type',
        accessor: 'slopeType',
        Cell: ({ value }) => <span>{getText(hangerData.slopeType.options, value)}</span>,
      },
      {
        Header: 'Slope Angle',
        accessor: 'slopeAngle',
      },
    ])

  const leftHipColumns = memberColumns(leftHip, trussData, 'leftHip')
    .concat(loadsColumns)
    .concat(angleColumns)

  const rightHipColumns = memberColumns(rightHip, trussData, 'rightHip')
    .concat(loadsColumns)
    .concat(angleColumns)
    .concat([
      {
        Header: 'isDuplicate',
        accessor: 'isDuplicate',
        Cell: ({ value }) => <span>{value ? 'Yes' : 'No'}</span>,
      },
    ])

  const hangerOptionsColumns = [
    {
      Header: 'Skew Type',
      accessor: 'skewType',
      Cell: ({ value }) => <span>{getText(hangerData.skewType.options, value)}</span>,
    },
    {
      Header: 'Skew (Degrees)',
      accessor: 'skewAngle',
    },
    {
      Header: 'Slope Type',
      accessor: 'slopeType',
      Cell: ({ value }) => <span>{getText(hangerData.slopeType.options, value)}</span>,
    },
    {
      Header: 'Slope (Degrees)',
      accessor: 'slopeAngle',
    },
    {
      Header: 'Open Closed Type',
      accessor: 'topFlangeOpenClosedType',
      Cell: ({ value }) => <span>{getText(hangerData.bendType.options, value)}</span>,
    },
    {
      Header: 'Top Flange Bend (Degrees)',
      accessor: 'topFlangeOpenClosedAngle',
    },
    {
      Header: 'Sloped Down Type',
      accessor: 'topFlangeSlopedDownType',
      Cell: ({ value }) => <span>{getText(hangerData.slopeDownType.options, value)}</span>,
    },
    {
      Header: 'Top Flange Slope (Degrees)',
      accessor: 'topFlangeSlopedDownAngle',
    },
    {
      Header: 'Offset Direction (Top Flange Only)',
      accessor: 'topFlangeOffset',
      Cell: ({ value }) => <span>{getText(hangerData.offsetType.options, value)}</span>,
    },
    {
      Header: 'High, Low, Center Flush',
      accessor: 'hlcFlush',
      Cell: ({ value }) => <span>{getText(hangerData.hlcFlush.options, value)}</span>,
    },
  ]

  return {
    jobSettingsColumns,
    settingColumns,
    isRoughColumn,
    loadsColumns,
    angleColumns,
    headerColumns,
    joistColumns,
    girderColumns,
    trussColumns,
    jackColumns,
    leftHipColumns,
    rightHipColumns,
    hangerOptionsColumns,
  }
}

const PrintContent = ({ modelImg }) => {
  const state = useStore().getState()
  const settings = getSettingsState(state)
  const { connectionType } = settings
  const jobSettings = getJobSettingsState(state)
  const { header, girder, joist, truss, jack, leftHip, rightHip } = getCalcInputState(state)
  const hangerOptions = getHangerState(state)

  const {
    jobSettingsColumns,
    settingColumns,
    headerColumns,
    joistColumns,
    girderColumns,
    trussColumns,
    jackColumns,
    leftHipColumns,
    rightHipColumns,
    hangerOptionsColumns,
  } = getPrintColumns({ header, girder, joist, truss, jack, leftHip, rightHip, settings })

  return (
    <>
      <div className="space-y-2">
        <div className="d-flex">
          <div style={{ flexGrow: 1 }}>
            <div>
              <h2>Input</h2>
              <div className="mt-4">
                <h3>Settings</h3>
                <PrintTable columns={settingColumns} data={[settings]} />
              </div>
            </div>
            <div className="mt-6">
              <h3>Job Settings</h3>
              <PrintTable columns={jobSettingsColumns} data={[jobSettings]} />
            </div>
          </div>

          <div className="pl-5">
            <h2>3D Model</h2>
            <div className="model-screenshot">
              <img width={300} height={300} src={modelImg} alt="3D-Viewer" />
            </div>
          </div>
        </div>

        {RenderIf(
          connectionType === 'joist',
          <div className="space-y-6">
            <h3>Header</h3>
            <PrintTable columns={headerColumns} data={[header]} />
            <div>
              <h3>Joist</h3>
              <PrintTable columns={joistColumns} data={[joist]} />
            </div>
            <div>
              <h3>Hanger Options</h3>
              <PrintTable columns={hangerOptionsColumns} data={[hangerOptions]} />
            </div>
          </div>,
        )}

        {RenderIf(
          connectionType === 'truss',
          <div className="space-y-6">
            <div>
              <h3>Girder</h3>
              <PrintTable columns={girderColumns} data={[girder]} />
            </div>
            <div>
              <h3>Truss</h3>
              <PrintTable columns={trussColumns} data={[truss]} />
            </div>
            <div>
              <h3>Hanger Options</h3>
              <PrintTable columns={hangerOptionsColumns} data={[hangerOptions]} />
            </div>
          </div>,
        )}

        {RenderIf(
          connectionType === 'multiTruss',
          <div className="space-y-6">
            <h3>Girder</h3>
            <PrintTable columns={girderColumns} data={[girder]} />

            {RenderIf(
              jobSettings.configuration !== 'leftRight',
              <div>
                <h3>Jack</h3>
                <PrintTable columns={jackColumns} data={[jack]} />
              </div>,
            )}
            {RenderIf(
              jobSettings.configuration !== 'centerRight',
              <div>
                <h3>Left Hip</h3>
                <PrintTable columns={leftHipColumns} data={[leftHip]} />
              </div>,
            )}
            {RenderIf(
              jobSettings.configuration !== 'leftCenter',
              <div>
                <h3>Right Hip</h3>
                <PrintTable columns={rightHipColumns} data={[rightHip]} />
              </div>,
            )}
          </div>,
        )}
      </div>
      <div className="page-break" />

      <div className="space-y-2">
        <div>
          <h2>Output</h2>
          <div className="mt-4">
            <h3>Result</h3>
            <Result />
          </div>
          <div className="page-break" />
        </div>
      </div>
    </>
  )
}

const PrintJobContent = ({ jobItem }) => {
  const {
    hangerOptions,
    jobSettings,
    header,
    girder,
    joist,
    truss,
    jack,
    leftHip,
    rightHip,
    settings,
  } = jobItem.jobInputs

  const {
    jobSettingsColumns,
    settingColumns,
    headerColumns,
    joistColumns,
    girderColumns,
    trussColumns,
    jackColumns,
    leftHipColumns,
    rightHipColumns,
    hangerOptionsColumns,
  } = getPrintColumns(jobItem.jobInputs)

  return (
    <>
      <div className="space-y-2">
        <div className="d-flex">
          <div style={{ flexGrow: 1 }}>
            <h2>{`${jobSettings.jobID} inputs`}</h2>
            <h2>Input</h2>
            <div className="mt-4">
              <h3>Settings</h3>
              <PrintTable columns={settingColumns} data={[settings]} />
            </div>
            <div className="mt-6">
              <h3>Job Settings</h3>
              <PrintTable columns={jobSettingsColumns} data={[jobSettings]} />
            </div>
          </div>
          <div className="pl-5">
            <h2>3D Model</h2>
            <div className="model-screenshot">
              <img width={300} height={300} src={jobItem.modelImg} alt="3D-Viewer" />
            </div>
          </div>
        </div>

        {RenderIf(
          settings.connectionType === 'joist',
          <div className="space-y-6">
            <h3>Header</h3>
            <PrintTable columns={headerColumns} data={[header]} />
            <div>
              <h3>Joist</h3>
              <PrintTable columns={joistColumns} data={[joist]} />
            </div>
            <div>
              <h3>Hanger Options</h3>
              <PrintTable columns={hangerOptionsColumns} data={[hangerOptions]} />
            </div>
            <div className="page-break" />
          </div>,
        )}

        {RenderIf(
          settings.connectionType === 'truss',
          <div className="space-y-6">
            <div>
              <h3>Girder</h3>
              <PrintTable columns={girderColumns} data={[girder]} />
            </div>
            <div>
              <h3>Truss</h3>
              <PrintTable columns={trussColumns} data={[truss]} />
            </div>
            <div>
              <h3>Hanger Options</h3>
              <PrintTable columns={hangerOptionsColumns} data={[hangerOptions]} />
            </div>
            <div className="page-break" />
          </div>,
        )}

        {RenderIf(
          settings.connectionType === 'multiTruss',
          <div className="space-y-6">
            <div>
              <h3>Girder</h3>
              <PrintTable columns={girderColumns} data={[girder]} />
            </div>
            {RenderIf(
              jobSettings.configuration !== 'leftRight',
              <div>
                <h3>Jack</h3>
                <PrintTable columns={jackColumns} data={[jack]} />
              </div>,
            )}
            {RenderIf(
              jobSettings.configuration !== 'centerRight',
              <div>
                <h3>Left Hip</h3>
                <PrintTable columns={leftHipColumns} data={[leftHip]} />
              </div>,
            )}
            {RenderIf(
              jobSettings.configuration !== 'leftCenter',
              <div>
                <h3>Right Hip</h3>
                <PrintTable columns={rightHipColumns} data={[rightHip]} />
              </div>,
            )}
            <div className="page-break" />
          </div>,
        )}
      </div>
      <div className="space-y-2">
        <div>
          <h2>{`${jobSettings.jobID} output`}</h2>
          <h2>Output</h2>
          <div className="mt-4">
            <h3>Result</h3>
            <Result jobItem={jobItem} />
          </div>
          <div className="page-break" />
        </div>
      </div>
    </>
  )
}

const PrintView = () => {
  const printRef = useRef()
  const { dispatch, getState } = useStore()
  const state = getState()
  const jobs = selectAll(state)

  const [modelImg, setModelImg] = useState()
  const [isPrinting, setPrinting] = useState(false)

  const handlePrintAction = useReactToPrint({
    documentTitle: APP_CONFIG.appName,
    onBeforeGetContent: async () => {
      dispatch(printAction())
      const hsModel = document.querySelector('bp-hs-legacy')
      if (jobs.length === 0 && hsModel) {
        const img = await hsModel.handleCaptureMesh()
        setModelImg(img)
      }

      setPrinting(true)
    },
    content: () => printRef.current,
    onAfterPrint: () => {
      setModelImg()
      setPrinting(false)
      dispatch(cancelPrintAction())
    },
  })

  return (
    <>
      <PrintButton onClick={handlePrintAction} />
      {RenderIf(
        isPrinting,
        <PrintWrapper ref={printRef}>
          <div className="print-header">
            <div className="appBrandingGroup">
              <SvgIcon src={APP_CONFIG.logoUrl} svgClasses="appLogo" alt={APP_CONFIG.appName} />
              <div className="aboutAppHeading">
                <div className="aboutAppAcronym">
                  <span className="aboutAppAcronym__label"></span>
                  {APP_CONFIG.appCode}
                </div>
                <div className="aboutAppFullName">{APP_CONFIG.appName}</div>
              </div>
            </div>
          </div>
          <div className="print-body">
            <div className="space-y-6">
              {RenderIf(
                jobs.length > 0,
                <div className="space-y-2">
                  <div>
                    <h3>Job List</h3>
                    <JobTally pdfFormat />
                  </div>
                  <div className="page-break" />
                </div>,
              )}

              {RenderIf(
                jobs.length > 0,
                jobs.map((item, index) => <PrintJobContent key={index} jobItem={item} />),
              )}

              {RenderIf(jobs.length === 0, <PrintContent modelImg={modelImg} />)}
            </div>
          </div>
        </PrintWrapper>,
      )}
    </>
  )
}

export default PrintView
